export const ENROLLMENT_CATEGORY_ID = 'enrollment_trends';
export const QUALITY_CATEGORY_ID = 'school_quality';
export const ENROLLMENT_TOUR_STORAGE_KEY = 'cityExplorer:enrollmentMapTour:v1';
export const QUALITY_TOUR_STORAGE_KEY = 'cityExplorer:qualityMapTour:v1';

export const isEnrollmentCategoryId = (id) => String(id || '').toLowerCase() === ENROLLMENT_CATEGORY_ID;

export const isQualityCategoryId = (id) => String(id || '').toLowerCase() === QUALITY_CATEGORY_ID;

export const locationHasEnrollmentMaps = (categories = []) =>
  categories.some((category) => isEnrollmentCategoryId(category?.id));

export const locationHasQualityMaps = (categories = []) =>
  categories.some((category) => isQualityCategoryId(category?.id));

export const isEnrollmentMapItem = (item) =>
  item?.type === 'map' && isEnrollmentCategoryId(item.category?.id);

export const isQualityMapItem = (item) => item?.type === 'map' && isQualityCategoryId(item.category?.id);

const SECTOR_TOGGLE_STEP = {
  id: 'sector-toggle',
  title: 'Filter by sector',
  body: 'Use All / Charter / District at the top of the map to show one sector at a time, or both together.',
  spotlight: 'sector-toggle',
  spotlightLabel: 'All · Charter · District',
};

const EXPLORE_STEP = {
  id: 'explore',
  title: 'Explore the map',
  body: 'Hover a school for its name. Use the + and − buttons in the top left to zoom, or scroll or pinch.',
  spotlight: 'map-zoom',
  spotlightLabel: 'Zoom',
};

export const ENROLLMENT_TOUR_STEPS = [
  {
    id: 'pin-color',
    title: 'Enrollment trend',
    body: 'Each school is colored by how enrollment changed from 2020–21 to 2025–26. Green is growing, gray is stable, red is declining, and light gray is no data. The scale is in the legend at the lower left.',
    spotlight: 'legend-box',
  },
  {
    id: 'school-markers',
    title: 'School markers',
    body: 'Filled circles are charter schools; squares are district schools. The fill color is the enrollment trend — not a quality rating.',
    spotlight: 'map-schools-west',
    spotlightLabel: 'Circles and squares',
  },
  SECTOR_TOGGLE_STEP,
  EXPLORE_STEP,
  {
    id: 'school-popups',
    title: 'School details',
    body: 'Click a school marker to open a pop-up with enrollment, trend, and grades. The highlighted card is an example of what you will see.',
    spotlight: 'example-popup',
  },
];

export const QUALITY_TOUR_STEPS = [
  {
    id: 'pin-color',
    title: 'School quality',
    body: 'Pin color is combined proficiency. Green means both math and reading are At or Above City & State. Red means below or mixed. Gray means no data. The scale is in the legend at the lower left.',
    spotlight: 'legend-box',
  },
  {
    id: 'school-markers',
    title: 'School markers',
    body: 'Filled circles are charter schools; squares are district schools. The fill color is that quality rating — not enrollment trend.',
    spotlight: 'map-schools-west',
    spotlightLabel: 'Circles and squares',
  },
  SECTOR_TOGGLE_STEP,
  EXPLORE_STEP,
  {
    id: 'school-popups',
    title: 'School details',
    body: 'Click a school marker to open a pop-up with enrollment plus all four academic fields: math and reading proficiency and growth. The highlighted card is an example of what you will see.',
    spotlight: 'example-popup',
    popupVariant: 'quality',
  },
];

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch (_err) {
    return false;
  }
};

const writeFlag = (key) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch (_err) {
    // Private browsing or disabled storage should not break the map.
  }
};

export const readEnrollmentTourDismissed = () => readFlag(ENROLLMENT_TOUR_STORAGE_KEY);
export const writeEnrollmentTourDismissed = () => writeFlag(ENROLLMENT_TOUR_STORAGE_KEY);
export const readQualityTourDismissed = () => readFlag(QUALITY_TOUR_STORAGE_KEY);
export const writeQualityTourDismissed = () => writeFlag(QUALITY_TOUR_STORAGE_KEY);

const shouldShow = ({ hasCategory, viewingMap, mapLoaded, isFullscreen, dismissed, replaying }) =>
  Boolean(hasCategory && viewingMap && mapLoaded && !isFullscreen && (!dismissed || replaying));

export const shouldShowEnrollmentTour = ({
  hasEnrollmentCategory,
  viewingEnrollmentMap,
  mapLoaded,
  isFullscreen,
  dismissed,
  replaying,
}) =>
  shouldShow({
    hasCategory: hasEnrollmentCategory,
    viewingMap: viewingEnrollmentMap,
    mapLoaded,
    isFullscreen,
    dismissed,
    replaying,
  });

export const shouldShowQualityTour = ({
  hasQualityCategory,
  viewingQualityMap,
  mapLoaded,
  isFullscreen,
  dismissed,
  replaying,
}) =>
  shouldShow({
    hasCategory: hasQualityCategory,
    viewingMap: viewingQualityMap,
    mapLoaded,
    isFullscreen,
    dismissed,
    replaying,
  });
